import type {
  ChannexPublicationResponse,
  ObjectParentBindingResponse,
  PropertyGroupResponse,
} from '@/shared/api';

export interface PublicationBadge {
  label: string;
  /** Dieselbe Aussage für schmale Flächen. */
  short: string;
  tone: 'ok' | 'progress' | 'error' | 'neutral';
}

const NOT_PUBLISHED: PublicationBadge = {
  label: 'Nicht veröffentlicht',
  short: 'Nicht veröffentlicht',
  tone: 'neutral',
};
const PUBLISHED: PublicationBadge = {
  label: 'Beim Kanalmanager veröffentlicht',
  short: 'Veröffentlicht',
  tone: 'ok',
};
const FIRST_RUN: PublicationBadge = {
  label: 'Veröffentlichung läuft',
  short: 'Wird veröffentlicht',
  tone: 'progress',
};
const FIRST_FAILED: PublicationBadge = {
  label: 'Veröffentlichung fehlgeschlagen',
  short: 'Fehlgeschlagen',
  tone: 'error',
};
const RECONCILE: PublicationBadge = {
  label: 'Veröffentlichung: Klärung nötig',
  short: 'Klärung nötig',
  tone: 'error',
};

/**
 * Der Stand beim Kanalmanager in einem Wort. Ob eine Änderung unterwegs oder liegengeblieben ist,
 * weiß nur der Server — wer den Veröffentlichungsbereich sieht, bekommt deshalb dieselbe Aussage
 * wie dort und bis zu ihrem Eintreffen keine. Ohne diesen Bereich genügt die Bindung.
 */
export function objectPublicationBadge(
  group: PropertyGroupResponse,
  publication: ChannexPublicationResponse | undefined,
  resolving: boolean,
): PublicationBadge | null {
  if (publication) return fromServer(publication);
  return resolving ? null : fromBinding(group.parentBinding);
}

function fromServer({ parentState, channexPropertyId }: ChannexPublicationResponse) {
  const exists = channexPropertyId !== null;
  switch (parentState) {
    case 'Published':
      return PUBLISHED;
    case 'Publishing':
      return exists
        ? { label: 'Änderung wird übertragen', short: 'Wird übertragen', tone: 'progress' as const }
        : FIRST_RUN;
    case 'Failed':
      return exists
        ? {
            label: 'Letzte Änderung nicht übernommen',
            short: 'Nicht übernommen',
            tone: 'error' as const,
          }
        : FIRST_FAILED;
    case 'NeedsReconcile':
      return RECONCILE;
    default:
      return NOT_PUBLISHED;
  }
}

function fromBinding(binding: ObjectParentBindingResponse | null | undefined): PublicationBadge {
  if (!binding) return NOT_PUBLISHED;
  // Das Objekt steht beim Kanalmanager; eine neuere, noch offene Fassung ändert daran nichts.
  if (binding.status === 'Succeeded') return PUBLISHED;
  if (binding.status === 'RequiresManualReconcile') return RECONCILE;
  if (binding.status === 'Failed') return FIRST_FAILED;
  return FIRST_RUN;
}
